import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useParams } from "react-router-dom";
import { getInitialFormState } from "../utils/transformers/automationTransformers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useAutomations from "../hooks/automationhooks/useAutomations";
import FieldHelpTooltip from "../components/common/FieldHelpTooltip";
import { journeyCreationTooltips } from "../utils/workflow/journeyTooltips";

const containsText = (text, searchText) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

export default function CreateAutomationForm({
  automationData: initialAutomationData,
  setAutomationData: setParentAutomationData,
}) {
  const { brandId: urlBrandId, automationId: urlAutomationId } = useParams();

  const isEditMode = Boolean(urlAutomationId);
  const isCreateWithBrand = Boolean(urlBrandId && !urlAutomationId);
  const isBrandFieldLocked = isEditMode || isCreateWithBrand;

  const [formData, setFormData] = useState(() => {
    const initial = initialAutomationData || getInitialFormState();

    if (isCreateWithBrand && urlBrandId) {
      return { ...initial, brandId: parseInt(urlBrandId) };
    }
    if (isEditMode) {
      return initial;
    }
    return { ...initial, brandId: null };
  });

  const [touched, setTouched] = useState([]);

  const { brands, handleGetBrands, handleGetBrandById } = useAutomations();
  const [brandName, setBrandName] = useState("");
  const [loadingBrands, setLoadingBrands] = useState(false);

  const [brandSearchText, setBrandSearchText] = useState("");

  const brandsLoadedRef = useRef(false);
  const brandIdLoadedRef = useRef(null);

  // Load brands ONCE on mount (only if field is editable)
  useEffect(() => {
    if (!isBrandFieldLocked && !brandsLoadedRef.current) {
      brandsLoadedRef.current = true;
      setLoadingBrands(true);
      handleGetBrands()
        .catch((err) => {
          brandsLoadedRef.current = false;
        })
        .finally(() => {
          setLoadingBrands(false);
        });
    }
  }, []);

  // Load single brand name if field is locked
  useEffect(() => {
    const brandIdToLoad = urlBrandId || formData.brandId;

    if (
      isBrandFieldLocked &&
      brandIdToLoad &&
      brandIdToLoad !== brandIdLoadedRef.current
    ) {
      brandIdLoadedRef.current = brandIdToLoad;
      setLoadingBrands(true);

      handleGetBrandById(brandIdToLoad)
        .then((brand) => {
          if (brand) {
            const name = brand.Name || brand.name;
            const displayName = !name || name.trim() === "" ? "No Name" : name;
            setBrandName(displayName);
          }
        })
        .catch((err) => {
          console.error("Error fetching brand:", err);
        })
        .finally(() => {
          setLoadingBrands(false);
        });
    }
  }, []);

  const handleInputChange = (field, value) => {
    const newData = {
      ...formData,
      [field]: value,
      schedule: formData.schedule || {},
      settings: formData.settings || {},
      metrics: formData.metrics || { Executions: 0, Successes: 0, Failures: 0 },
      actions: formData.actions || {
        Id: 0,
        Type: "",
        To: "",
        Subject: "",
        Body: "",
        Attachments: "",
      },
    };
    setFormData(newData);
    setParentAutomationData?.(newData);
  };

  const handleScheduleChange = (field, value) => {
    const newData = {
      ...formData,
      schedule: {
        ...formData.schedule,
        [field]: value ? value.toISOString() : null,
        LastRun: formData.schedule?.start || value,
      },
    };
    setFormData(newData);
    setParentAutomationData?.(newData);
  };

  const handleTouched = (field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  // Filter brands based on search text
  const filteredBrands = React.useMemo(() => {
    if (!Array.isArray(brands)) return [];

    const searchLower = brandSearchText.toLowerCase();
    return brands.filter((brand) => {
      const name = brand.Name || brand.name || "";
      const displayName = !name || name.trim() === "" ? "No Name" : name;
      const id = String(brand.Id || brand.id || "");

      return (
        containsText(displayName, searchLower) || containsText(id, searchLower)
      );
    });
  }, [brands, brandSearchText]);

  // Get selected brand display text for the closed Select field
  const selectedBrandDisplayText = React.useMemo(() => {
    if (!formData.brandId) return "";
    if (!Array.isArray(brands) || brands.length === 0) return "";

    const selectedBrand = brands.find(
      (b) => (b.Id || b.id) === formData.brandId
    );
    if (!selectedBrand) return "";

    const name = selectedBrand.Name || selectedBrand.name || "";
    const brandName = !name || name.trim() === "" ? "No Name" : name;
    return `${brandName} [${formData.brandId}]`;
  }, [formData.brandId, brands]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Brand Name Field - Conditional rendering based on lock state */}
      {isBrandFieldLocked ? (
        // Locked Mode: Read-only display (Edit mode OR creating from brand page)
        <TextField
          margin="dense"
          label="Brand"
          type="text"
          fullWidth
          value={
            brandName
              ? `${brandName} [${urlBrandId || formData.brandId}]`
              : "Loading..."
          }
          disabled
          sx={{
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "rgba(255, 255, 255, 0.7)",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
            },
          }}
        />
      ) : (
        // Editable Create Mode: Dropdown with integrated search
        <FormControl
          fullWidth
          margin="dense"
          required
          onBlur={() => handleTouched("brandId")}
          error={!formData.brandId && touched.brandId}
          disabled={loadingBrands}
        >
          <InputLabel>Brand</InputLabel>
          <Select
            value={formData.brandId || ""}
            onChange={(e) => {
              console.log("Brand selected:", e.target.value);
              handleInputChange("brandId", e.target.value);
              setBrandSearchText("");
            }}
            renderValue={(selected) => {
              if (!selected) return "";
              return selectedBrandDisplayText || `Loading... [${selected}]`;
            }}
            label="Brand"
            MenuProps={{
              autoFocus: false,
              PaperProps: {
                style: {
                  maxHeight: 48 * 8,
                },
              },
            }}
            onClose={() => setBrandSearchText("")}
            startAdornment={
              brandSearchText && (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }
          >
            {/* Search TextField as first MenuItem */}
            <MenuItem
              disableRipple
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "background.paper",
                "&:hover": {
                  backgroundColor: "background.paper",
                  cursor: "default",
                },
                "&.Mui-focusVisible": {
                  backgroundColor: "background.paper",
                },
              }}
            >
              <TextField
                size="small"
                autoFocus
                placeholder="Filter..."
                fullWidth
                value={brandSearchText}
                onChange={(e) => setBrandSearchText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") {
                    e.stopPropagation();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </MenuItem>

            <MenuItem value="">
              <em>Select a brand</em>
            </MenuItem>

            {/* Filtered brand list */}
            {Array.isArray(filteredBrands) && filteredBrands.length > 0 ? (
              filteredBrands.map((brand) => {
                const brandId = brand.Id || brand.id;
                const brandNameRaw = brand.Name || brand.name || "";
                const brandName =
                  !brandNameRaw || brandNameRaw.trim() === ""
                    ? "No Name"
                    : brandNameRaw;
                const displayText = `${brandName} [${brandId}]`;

                return (
                  <MenuItem key={brandId} value={brandId}>
                    {displayText}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem value="" disabled>
                {loadingBrands
                  ? "Loading brands..."
                  : brandSearchText
                  ? "No matching brands"
                  : "No brands available"}
              </MenuItem>
            )}
          </Select>
          <FormHelperText>
            {!formData.brandId && touched.brandId ? "Brand is required" : ""}
          </FormHelperText>
        </FormControl>
      )}

      {/* Name */}
      <TextField
        margin="dense"
        label="Name"
        type="text"
        fullWidth
        placeholder="New Journey"
        required
        value={formData.name}
        onChange={(e) => handleInputChange("name", e.target.value)}
        onBlur={(e) => {
          handleTouched("name");
        }}
        error={!formData.name && touched.name}
        helperText={touched.name && !formData.name ? "Name is required" : ""}
        InputProps={{
          endAdornment: (
            <FieldHelpTooltip title={journeyCreationTooltips.name} placement="left" />
          ),
        }}
      />

      {/* Description */}
      <TextField
        margin="dense"
        label="Description"
        type="text"
        fullWidth
        multiline
        rows={2}
        value={formData.description || ""}
        onBlur={() => handleTouched("description")}
        onChange={(e) => handleInputChange("description", e.target.value)}
        InputProps={{
          endAdornment: (
            <FieldHelpTooltip
              title={journeyCreationTooltips.description}
              placement="left"
              sx={{ alignSelf: "flex-start", mt: 1 }}
            />
          ),
        }}
      />

      {/* Status */}
      <Box sx={{ position: "relative" }}>
        <FieldHelpTooltip
          title={journeyCreationTooltips.status}
          placement="left"
          sx={{ position: "absolute", right: 8, top: 10, zIndex: 2 }}
        />
        <FormControl
          fullWidth
          margin="dense"
          required
          onBlur={(e) => {
            handleTouched("status");
          }}
          error={!formData.status && touched.status}
        >
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status || "Pending"}
            onChange={(e) => handleInputChange("status", e.target.value)}
            label="Status"
          >
          <MenuItem value="Disabled">Disabled</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
        <FormHelperText>
          {!formData.status && touched.status ? "Status is required" : ""}
        </FormHelperText>
        </FormControl>
      </Box>

      {/* Priority */}
      <Box sx={{ position: "relative" }}>
        <FieldHelpTooltip
          title={journeyCreationTooltips.priority}
          placement="left"
          sx={{ position: "absolute", right: 8, top: 10, zIndex: 2 }}
        />
        <FormControl
          fullWidth
          margin="dense"
          required
          onBlur={(e) => {
            handleTouched("priority");
          }}
          error={!formData.priority && touched.priority}
        >
          <InputLabel>Priority</InputLabel>
          <Select
            value={formData.priority || "Low"}
            onChange={(e) => handleInputChange("priority", e.target.value)}
            label="Priority"
          >
          <MenuItem value="High" sx={{ color: "error.main" }}>
            High
          </MenuItem>
          <MenuItem value="Low" sx={{ color: "success.main" }}>
            Low
          </MenuItem>
        </Select>
        <FormHelperText>
          {!formData.priority && touched.priority ? "Priority is required" : ""}
        </FormHelperText>
        </FormControl>
      </Box>

      {/* Start Date */}
      <Box sx={{ position: "relative" }}>
        <FieldHelpTooltip
          title={journeyCreationTooltips.scheduleStart}
          placement="left"
          sx={{ position: "absolute", right: 8, top: 10, zIndex: 2 }}
        />
        <DateTimePicker
          label="Start Date"
        value={
          formData.schedule?.start ? dayjs(formData.schedule.start) : dayjs()
        }
        onChange={(newValue) => handleScheduleChange("start", newValue)}
        ampm={false}
        minDateTime={dayjs()}
        slotProps={{
          textField: {
            margin: "dense",
            fullWidth: true,
            required: true,
            onBlur: () => handleTouched("scheduleStart"),
            error:
              touched.scheduleStart &&
              (!formData.schedule?.start ||
                dayjs(formData.schedule.start).isBefore(dayjs())),
            helperText:
              touched.scheduleStart &&
              (!formData.schedule?.start
                ? "Start date is required"
                : dayjs(formData.schedule.start).isBefore(dayjs())
                ? "Start date cannot be in the past"
                : ""),
          },
          popper: {
            sx: {
              "& .MuiPickersLayout-root": {
                display: "grid",
                gridTemplateColumns: "1fr auto",
                minWidth: "200px",
              },
              "& .MuiPickersLayout-actionBar": {
                gridColumn: "2/3",
              },
            },
          },
        }}
        />
      </Box>

      {/* End Date */}
      <Box sx={{ position: "relative" }}>
        <FieldHelpTooltip
          title={journeyCreationTooltips.scheduleEnd}
          placement="left"
          sx={{ position: "absolute", right: 8, top: 10, zIndex: 2 }}
        />
        <DateTimePicker
          label="End Date"
        value={formData.schedule?.end ? dayjs(formData.schedule.end) : null}
        onChange={(newValue) => handleScheduleChange("end", newValue)}
        ampm={false}
        minDateTime={
          formData.schedule?.start ? dayjs(formData.schedule.start) : dayjs()
        }
        slotProps={{
          textField: {
            margin: "dense",
            fullWidth: true,
            onBlur: () => handleTouched("scheduleEnd"),
          },
          popper: {
            sx: {
              "& .MuiPaper-root": {
                padding: "0 8px",
              },
            },
          },
        }}
        />
      </Box>
    </LocalizationProvider>
  );
}
